import { Plus, Bus, Train, ImageIcon } from "lucide-react";
import { Link } from "@remix-run/react";
import { propertySlug } from "~/lib/helpers/slugHelper";
import { cn } from "~/lib/utils";

type PropertyCardProps = {
  property: any;
  showColView?: boolean;
  onShortlist?: () => void;
  isShortlisted?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export function PropertyCard({
  property,
  showColView = false,
  onShortlist,
  isShortlisted = false,
  onMouseEnter,
  onMouseLeave,
}: PropertyCardProps) {
  const image = property.imageUrl || property.imageURL1 || property.image || property.assets?.[0]?.url || "";
  const title = property.buildingName || property.address || property.name;
  const location = property.addressFormula || property.location || "";
  const currencyIsoCode = property.currencyIsoCode || "INR";
  const propertyLink = propertySlug(property);

  const parsePrice = (val?: number | string): number => {
    if (typeof val === "number") return val;
    if (typeof val === "string") return parseInt(val.replace(/[^0-9]/g, ""), 10) || 0;
    return 0;
  };

  const displayPrice =
    parsePrice(property.costPerWorkstation) ||
    parsePrice(property.deskMonthlyPrice) ||
    parsePrice(property.priceMonthly) ||
    parsePrice(property.advertisedPriceWeekly) ||
    parsePrice(property.price);

  const formattedPrice = new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currencyIsoCode,
    maximumFractionDigits: 0,
  }).format(displayPrice);

  const busName = property.nearestBus?.name || property.closestBusStopName || "";
  const busTime =
    property.nearestBus?.travelTimeModeString ||
    property.nearestBus?.travelTimeFromProperty ||
    (property.closestBusStopTravelTime ? `${property.closestBusStopTravelTime} mins walk` : "");

  const trainName = property.nearestRail?.name || property.closestTrainStationName || "";
  const trainTime =
    property.nearestRail?.travelTimeModeString ||
    property.nearestRail?.travelTimeFromProperty ||
    (property.closestTrainStationTravelTime ? `${property.closestTrainStationTravelTime} mins walk` : "");

  const logoUrl = property.workspaceProvider?.partnerLogo || property.partnerLogo || property.providerLogo;

  const handleShortlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onShortlist?.();
  };

  return (
    <Link
      to={propertyLink}
      target="_blank"
      className={cn("block", showColView ? "w-full min-w-[260px] max-w-[340px]" : "w-full")}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={cn(
          "flex w-full rounded-xl border bg-white shadow-sm transition hover:shadow-md overflow-hidden cursor-pointer",
          showColView ? "min-w-[260px] max-w-[340px] flex-col" : "flex-col lg:flex-row",
        )}
      >
        <div
          className={cn(
            "relative flex-shrink-0 overflow-hidden bg-gray-100 border",
            showColView ? "w-full aspect-[5/3] min-h-[120px] max-h-[180px]" : "w-full h-[166px] lg:w-56",
          )}
        >
          <img src={image} alt={title} className="h-full w-full object-cover" />
          <div className="absolute bottom-1 right-1 flex items-center gap-1 bg-black/50 p-1 py-0.5 rounded-md cursor-pointer ">
            <ImageIcon className="h-3 w-3 text-white" />
            <span className="text-xs text-white">{property.assets?.length}</span>
          </div>
        </div>

        <div className="flex flex-1 flex-col justify-between gap-1 min-w-0 pt-2">
          <div className="flex gap-2 items-center px-4">
            {logoUrl && (
              <div className="flex items-center mb-1 border border-gray-300 p-1 min-h-10 min-w-10 rounded-full overflow-hidden">
                <img src={logoUrl} alt="Provider logo" className="h-8 w-8 rounded-full object-contain" />
              </div>
            )}

            <div className="flex flex-col gap-1 leading-tight">
              <h3 className="text-sm font-semibold text-gray-900 line-clamp-1">{title}</h3>
              <div className="text-[12px] text-oh-grey-heavy line-clamp-1">{location}</div>
            </div>
          </div>

          {!showColView && (
            <div className="flex flex-col gap-1.5 px-4 leading-tight mt-1">
              {busName && busTime && (
                <div className="flex items-center gap-1 text-xs text-oh-grey-heavy">
                  <div className="flex items-center gap-1">
                    <Bus className="h-4 w-4 text-oh-grey-heavy flex-shrink-0" />
                    <span className="text-xs text-oh-grey-heavy">(B)</span>
                  </div>
                  <span className="truncate font-medium">
                    {busName}. {busTime}
                  </span>
                </div>
              )}
              {trainName && trainTime && (
                <div className="flex items-center gap-1 text-xs text-oh-grey-heavy">
                  <div className="flex items-center gap-1">
                    <Train className="h-4 w-4 text-oh-grey-heavy flex-shrink-0" />
                    <span className="text-xs text-oh-grey-heavy">(T)</span>
                  </div>
                  <span className="truncate font-medium">
                    {trainName}. {trainTime}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="flex items-between justify-between bg-gray-50 py-[6px] mt-1  px-4">
            <div className="flex gap-1 items-center">
              <div className="text-xl font-bold text-oh-black">{formattedPrice}</div>
              <div className="text-xs font-medium text-oh-grey-heavy">per desk /mth</div>
            </div>

            <button
              onClick={handleShortlistClick}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-2 py-2 text-xs font-medium transition-colors",
                isShortlisted
                  ? "bg-oh-primary text-white hover:bg-oh-primary/50"
                  : "bg-white text-oh-primary hover:bg-oh-primary hover:text-white hover:border-oh-primary border border-oh-primary ",
              )}
            >
              <Plus className="h-4 w-4" />
              <span>{isShortlisted ? "Shortlisted" : "Shortlist"}</span>
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

